import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, useQueryClient } from "react-query";
import { useRecoilValue } from "recoil";
import { toast } from "src/components";
import { authState } from "src/recoils";
import {
  GetWebsiteSystem,
  GetWebsiteSystemAudit,
  ToggleWebsiteSystem,
  UpdateWebsiteSystem,
  UpdateWebsiteSystemRequest,
  WebsiteSystemAuditItem,
  WebsiteSystemFeatureItem,
  WebsiteSystemProduct,
  WebsiteSystemProductEnabled,
  WebsiteSystemScope,
  WEBSITE_SYSTEM_PRODUCTS,
} from "src/services/websiteSystem";

const WEBSITE_SYSTEM_QUERY_KEY = "websiteSystem";
const WEBSITE_SYSTEM_AUDIT_QUERY_KEY = "websiteSystemAudit";
// The audit GET has no `total` in its response, so paging stops the moment a page
// comes back shorter than the page size instead of counting against a known total.
const AUDIT_PAGE_SIZE = 20;

// axios rejects the request promise itself on a 4xx/5xx (before any
// `response.service_code` in a resolved body could be checked), carrying the
// backend's own service_message on `error.response.data` - Thai for some
// endpoints' errors, plain English for others (e.g. the schedule/site checks), so
// it is shown as-is rather than assumed to be Thai. Without this, a
// query/mutation's catch block sees axios's generic "Request failed with status
// code 400" instead. Falls back to the JS Error's own message, then to the
// caller-supplied Thai default.
function extractServiceMessage(error: unknown): string | undefined {
  const responseMessage = (error as { response?: { data?: { service_message?: string } } })?.response?.data
    ?.service_message;
  if (responseMessage) return responseMessage;
  if (error instanceof Error) return error.message;
  return undefined;
}

// The day/time fields exactly as the API sent them. The mapped fields of
// WebsiteSystemItem split days_week, so a caller that resends a row's schedule
// without letting the admin edit it reads these instead - the renew-range dialog,
// and the edit modal for the schedule fields its chosen type does not use - or its
// PUT would rewrite fields nobody touched and leave audit rows for them.
export type WebsiteSystemStoredFields = Pick<
  WebsiteSystemFeatureItem,
  "days_week" | "start_datetime" | "end_datetime"
>;

export type WebsiteSystemItem = {
  code: string;
  name: string;
  scope: WebsiteSystemScope;
  enabled: boolean;
  products: WebsiteSystemProductEnabled[];
  typeTime: string;
  daysWeek: string[];
  startDatetime?: string;
  endDatetime?: string;
  title?: string;
  message: string;
  linkUrl?: string;
  linkLabel?: string;
  toast: boolean;
  notification: boolean;
  notificationType: string;
  updatedAt?: string;
  updatedBy?: string;
  stored: WebsiteSystemStoredFields;
};

const mapFeatureItem = (item: WebsiteSystemFeatureItem): WebsiteSystemItem => ({
  code: item.code,
  name: item.name,
  scope: item.scope,
  enabled: item.enabled,
  products: item.products,
  typeTime: item.type_time || "now",
  daysWeek: item.days_week ? item.days_week.split(",") : [],
  startDatetime: item.start_datetime || undefined,
  endDatetime: item.end_datetime || undefined,
  title: item.title || undefined,
  // message is nullable on the wire (a row that has never been through the group
  // PUT has no text yet) - the table/modal always want a plain string to render.
  message: item.message || "",
  linkUrl: item.link_url || undefined,
  linkLabel: item.link_label || undefined,
  toast: item.toast,
  notification: item.notification,
  notificationType: item.notification_type,
  updatedAt: item.updated_at || undefined,
  updatedBy: item.updated_by || undefined,
  stored: {
    days_week: item.days_week,
    start_datetime: item.start_datetime,
    end_datetime: item.end_datetime,
  },
});

export function useWebsiteSystem() {
  const { token = "" } = useRecoilValue(authState);
  return useQuery<WebsiteSystemItem[], Error>(
    [WEBSITE_SYSTEM_QUERY_KEY],
    async () => {
      try {
        const response = await GetWebsiteSystem({ token });
        return response.data.feature_list.map(mapFeatureItem);
      } catch (error) {
        throw new Error(extractServiceMessage(error) || "โหลดข้อมูลไม่สำเร็จ");
      }
    },
    { keepPreviousData: true }
  );
}

export type ToggleWebsiteSystemVariables = {
  code: string;
  enabled: boolean;
};

export function useToggleWebsiteSystem(
  options?: UseMutationOptions<unknown, unknown, ToggleWebsiteSystemVariables, unknown>
) {
  const { token = "" } = useRecoilValue(authState);
  const queryClient = useQueryClient();
  return useMutation(
    async (variables: ToggleWebsiteSystemVariables) => {
      try {
        return await ToggleWebsiteSystem({ token, ...variables });
      } catch (error) {
        throw new Error(extractServiceMessage(error) || "อัปเดตไม่สำเร็จ");
      }
    },
    {
      onError: (error) => toast.error((error as Error)?.message || "อัปเดตไม่สำเร็จ", { autoClose: 1000 }),
      onSuccess: (_data, variables) => {
        toast.success("อัปเดตสำเร็จ", { autoClose: 1000 });
        // resetQueries (not invalidateQueries) so an open history drawer drops
        // back to just its first page instead of refetching every previously
        // -loaded page at its old offset - the new audit row this toggle just
        // wrote shifts everything after it by one, so refetching page 2 at the
        // old offset would show its old last row a second time.
        queryClient.resetQueries([WEBSITE_SYSTEM_AUDIT_QUERY_KEY, variables.code]);
      },
      // The list is re-read whatever the outcome: after a success to show the new
      // state, and after a refusal because a refusal usually means this page's
      // copy of the row is out of date (e.g. its custom window ended after the
      // page loaded), so the next click must decide from fresh data rather than
      // repeat the same request. In onSettled so a caller that replaces
      // onSuccess/onError (the renew-range dialog does) keeps it; returned so the
      // mutation - and the switch - stays loading until the refetch has landed.
      onSettled: () => queryClient.invalidateQueries([WEBSITE_SYSTEM_QUERY_KEY]),
      ...options,
    }
  );
}

export type UpdateWebsiteSystemVariables = {
  code: string;
  scope: WebsiteSystemScope;
  // The selected sites - read for a PER_PRODUCT code only, ignored for ALL.
  products?: WebsiteSystemProduct[];
  typeTime: "now" | "schedule" | "custom";
  daysWeek?: string | null;
  startDatetime?: string;
  endDatetime?: string;
};

// Split out from the mutation so it can be unit-tested directly (no
// QueryClientProvider/RecoilRoot needed). A PER_PRODUCT code always carries its
// selected sites in display order, and throws before any request is made if
// none is selected (the modal's own form rule normally stops that first); an
// ALL-scope code never carries a `products` key at all. The text and the
// channel flags are not editable on this page (Core BO publishes the messages),
// so every save sends message "" - the backend requires the key - with both
// toast and notification false, and no title/link keys.
export function buildUpdateWebsiteSystemRequest(
  token: string,
  variables: UpdateWebsiteSystemVariables
): UpdateWebsiteSystemRequest {
  let products: WebsiteSystemProduct[] | undefined;
  if (variables.scope === "PER_PRODUCT") {
    const chosen = variables.products || [];
    products = WEBSITE_SYSTEM_PRODUCTS.filter((product) => chosen.includes(product));
    if (products.length === 0) throw new Error("กรุณาเลือกอย่างน้อยหนึ่งเว็บ");
  }
  return {
    token,
    code: variables.code,
    message: "",
    toast: false,
    notification: false,
    type_time: variables.typeTime,
    days_week: variables.daysWeek,
    start_datetime: variables.startDatetime,
    end_datetime: variables.endDatetime,
    ...(products ? { products } : {}),
  };
}

// No default onError here (unlike the toggle mutation above): a table toggle has
// no form to preserve, so a toast is enough, but the edit modal needs to show the
// backend's own validation message inline and stay open with the admin's input
// intact - see WebsiteSystemEditModal, which calls mutateAsync itself and reads
// error.response.data.service_message on rejection.
export type UpdateWebsiteSystemNotify = {
  // false: no "บันทึกสำเร็จ" toast - for a caller whose save is only the first
  // step of a larger action and reports the outcome once, at the end.
  successToast?: boolean;
};

export function useUpdateWebsiteSystem(
  options?: UseMutationOptions<unknown, unknown, UpdateWebsiteSystemVariables, unknown>,
  { successToast = true }: UpdateWebsiteSystemNotify = {}
) {
  const { token = "" } = useRecoilValue(authState);
  const queryClient = useQueryClient();
  return useMutation(
    (variables: UpdateWebsiteSystemVariables) => UpdateWebsiteSystem(buildUpdateWebsiteSystemRequest(token, variables)),
    {
      onSuccess: (_data, variables) => {
        if (successToast) toast.success("บันทึกสำเร็จ", { autoClose: 1000 });
        // See the same resetQueries/invalidateQueries notes in useToggleWebsiteSystem's onSuccess.
        queryClient.resetQueries([WEBSITE_SYSTEM_AUDIT_QUERY_KEY, variables.code]);
        return queryClient.invalidateQueries([WEBSITE_SYSTEM_QUERY_KEY]);
      },
      ...options,
    }
  );
}

export function useWebsiteSystemAudit(code: string, enabled: boolean) {
  const { token = "" } = useRecoilValue(authState);
  return useInfiniteQuery<WebsiteSystemAuditItem[], Error>(
    [WEBSITE_SYSTEM_AUDIT_QUERY_KEY, code],
    async ({ pageParam = 0 }) => {
      try {
        const response = await GetWebsiteSystemAudit({ token, code, limit: AUDIT_PAGE_SIZE, offset: pageParam });
        return response.data.audit_list;
      } catch (error) {
        throw new Error(extractServiceMessage(error) || "โหลดประวัติไม่สำเร็จ");
      }
    },
    {
      enabled,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < AUDIT_PAGE_SIZE) return undefined;
        return allPages.reduce((total, page) => total + page.length, 0);
      },
    }
  );
}
